import React from 'react';
import { useNavigate } from 'react-router-dom';
import RoleDashboardShell from '@/presentation/dashboard/RoleDashboardShell';
import { useRoleDashboardConfig } from '@/presentation/dashboard/useRoleDashboardConfig';
import { useDashboardHomeCopy } from '@/presentation/dashboard/dashboardHomeLocale';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import PublicIeltsQuizSpotlight from '@/presentation/quiz/PublicIeltsQuizSpotlight';

export default function AdminDashboard({ session, onLogout }) {
  const config = useRoleDashboardConfig(session);
  const t = useDashboardHomeCopy(session);
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [tickets, setTickets] = React.useState([]);
  const [loadingTickets, setLoadingTickets] = React.useState(false);
  const [ticketError, setTicketError] = React.useState('');
  const [ticketResult, setTicketResult] = React.useState('');
  const [processingTicketId, setProcessingTicketId] = React.useState('');
  const navigate = useNavigate();

  const loadTickets = React.useCallback(async () => {
    if (!token) return;
    setLoadingTickets(true);
    setTicketError('');
    try {
      const data = await api.listAdminSupportTickets(token);
      const resetTickets = (data?.items || []).filter((t) => t.category === 'reset_password');
      setTickets(resetTickets);
    } catch (e) {
      setTicketError(e.message || t.ticketLoadError);
    } finally {
      setLoadingTickets(false);
    }
  }, [api, token, t.ticketLoadError]);

  React.useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <div className="mx-auto max-w-7xl space-y-6">
        <PublicIeltsQuizSpotlight />
        <section className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="font-headline text-3xl font-extrabold">{t.heroTitle}</h1>
            <p className="mt-1 text-slate-600">{t.heroSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/quizzes/create')}
            className="rounded-xl bg-emerald-700 px-6 py-3 font-bold text-white shadow-lg shadow-emerald-200"
          >
            {t.createQuiz}
          </button>
        </section>

        <div className="grid gap-6 md:grid-cols-4">
          <MetricCard label={t.metricUsers} value="12,482" delta="+12%" tone="primary" />
          <MetricCard label={t.metricActive} value="3,241" delta="+5.4%" tone="secondary" />
          <MetricCard label={t.metricTraffic} value="84.2k" delta="+18%" tone="tertiary" />
          <article className="rounded-xl bg-slate-900 p-6 text-white shadow-xl">
            <p className="text-xs uppercase tracking-wider text-slate-300">{t.systemLabel}</p>
            <p className="mt-2 text-xl font-extrabold">{t.systemHealth}</p>
          </article>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <article className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm lg:col-span-2">
            <h2 className="font-headline text-xl font-bold">{t.activityTitle}</h2>
            <div className="mt-4 space-y-3">
              {t.activityRows.map((row) => (
                <div key={row[0]} className="grid grid-cols-4 gap-3 rounded-lg border border-slate-100 p-3 text-sm">
                  <div className="font-semibold">{row[0]}</div>
                  <div>{row[1]}</div>
                  <div className="text-slate-500">{row[2]}</div>
                  <div className="font-semibold">{row[3]}</div>
                </div>
              ))}
            </div>
          </article>

          <article className="space-y-6">
            <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
              <h3 className="font-headline text-lg font-bold">{t.userSplitTitle}</h3>
              <StatBar label={t.userStudent} width="65%" />
              <StatBar label={t.userTeacher} width="25%" />
              <StatBar label={t.userParent} width="10%" />
            </div>
            <div className="rounded-xl border border-slate-100 bg-cyan-50 p-6 shadow-sm">
              <h3 className="font-headline text-lg font-bold">{t.releaseTitle}</h3>
              <p className="mt-2 text-sm text-slate-600">{t.releaseBody}</p>
            </div>
          </article>
        </div>

        <article className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-headline text-xl font-bold">{t.ticketTitle}</h2>
              <p className="text-sm text-slate-500">{t.ticketSubtitle}</p>
            </div>
            <button
              type="button"
              onClick={loadTickets}
              className="rounded-lg border border-cyan-300 px-3 py-2 text-sm font-bold text-cyan-700"
            >
              {t.refresh}
            </button>
          </div>

          {ticketError ? (
            <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
              {ticketError}
            </p>
          ) : null}
          {ticketResult ? (
            <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              {ticketResult}
            </p>
          ) : null}

          {loadingTickets ? (
            <p className="text-sm text-slate-500">{t.loadingTickets}</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-slate-500">{t.noTickets}</p>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="rounded-lg border border-slate-100 p-4 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-bold text-slate-800">{ticket.user_name || t.ticketUserFallback}</p>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-bold ${
                        ticket.status === 'open'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {ticket.status}
                    </span>
                  </div>
                  <p className="mt-1 text-slate-600">Email: {ticket.user_email || 'N/A'}</p>
                  <p className="mt-1 text-slate-600">{ticket.message}</p>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-400">
                      Ticket: {ticket.id}
                    </p>
                    <button
                      type="button"
                      disabled={ticket.status !== 'open' || processingTicketId === ticket.id}
                      onClick={async () => {
                        if (!token) return;
                        setProcessingTicketId(ticket.id);
                        setTicketResult('');
                        setTicketError('');
                        try {
                          const result = await api.adminResetPasswordByTicket(ticket.id, token);
                          setTicketResult(t.formatTicketSuccess(result.user_email, ticket.id));
                          await loadTickets();
                        } catch (e) {
                          setTicketError(e.message || t.ticketErrorGeneric);
                        } finally {
                          setProcessingTicketId('');
                        }
                      }}
                      className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                    >
                      {processingTicketId === ticket.id ? t.ticketProcessing : t.ticketGrantDefault}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </div>
    </RoleDashboardShell>
  );
}

function MetricCard({ label, value, delta, tone }) {
  const toneClass =
    tone === 'secondary'
      ? 'text-emerald-700 bg-emerald-50'
      : tone === 'tertiary'
        ? 'text-rose-700 bg-rose-50'
        : 'text-cyan-700 bg-cyan-50';
  return (
    <article className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
      <span className={`rounded-full px-2 py-1 text-xs font-bold ${toneClass}`}>{delta}</span>
      <p className="mt-3 text-3xl font-black">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </article>
  );
}

function StatBar({ label, width }) {
  return (
    <div className="mt-4">
      <div className="mb-1 flex justify-between text-xs font-semibold text-slate-500">
        <span>{label}</span>
        <span>{width}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-cyan-600" style={{ width }} />
      </div>
    </div>
  );
}

